import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Box } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { TOGGLE_LAYOUT } from "../../../store/layout/Actions";
import {
  CustomBadge,
  CustomBreadCrumb,
  PageTitle,
  ReskinningTabs,
} from "../../../Components";
import {
  ClientProfileCard,
  AgentCard,
  LastActivityCard,
  ClientActionsCard,
  ActionsSkeletonLoader,
  ClientProfileSkeletonLoader,
  LastActivitySkeletonLoader,
  AssignedAgentSkeletonLoader,
  TransactionPanel,
  ActivityTimelinePanel,
  OffersPanel,
  AssignedAgentsPanel,
  DocumentsPanel,
  PaymentPanel,
} from "./UI";
import { ContactsMapper } from "../ContactsView";
import { GetAdvanceSearchContacts } from "../../../Services";
import {
  AddAfterSalesClientComment,
  GetAfterSalesAgentById,
  GetAfterSalesTransactionByContactId,
  GetCommentsByContactId,
} from "../../../Services/AfterSalesServices/AfterSalesServices";
import {
  useLayoutDetection,
  useSelectedTheme,
  useTranslate,
} from "../../../Hooks";

import useStyles from "./styles";
import { PermissionDeniedLayout } from "../../../Layouts/PermissionDeniedLayout/PermissionDeniedLayout";
import { AfterSalePermissions } from "../../../Permissions";
import { havePermissionToViewDetails } from "../../../Helper";
import { ContactOverviewCommentsCard } from "../NewContactsView/ContactOverview/UI/ContactOverviewSummary/UI";

export function AfterSalesClientProfileView() {
  const [contactDetails, setContactDetails] = useState({
    result: {},
    totalCount: 0,
  });
  const [isContactDetailsLoading, setIsContactDetailsLoading] = useState(false);
  const [isSalesAgentLoading, setIsSalesAgentLoading] = useState(false);
  const [refetchOffers, setRefetchOffers] = useState(false);
  const [tranActionDetails, setTranActionDetails] = useState({
    result: [],
    totalCount: 0,
  });
  const [salesAgent, setSalesAgent] = useState(null);
  const [tranActionDetailsLoading, setTranActionDetailsLoading] =
    useState(false);
  const location = useLocation();
  const contactId = new URLSearchParams(location.search).get("id");
  const getContactDetails = useCallback(async () => {
    setIsContactDetailsLoading(true);

    try {
      const isForLog = true;

      let body = {
        criteria: {
          Ids: [{ searchType: 1, value: contactId }],
        },
        filterBy: "createdOn",
        orderBy: 2,
      };

      const res = await GetAdvanceSearchContacts(
        { pageIndex: 0, pageSize: 2, isForLog },
        body
      );

      if (!(res && res.status && res.status !== 200)) {
        if (res && res.totalCount > 0) {
          if (
            res &&
            res.result &&
            res.result.length &&
            res.result[0].contactJson
          ) {
            setContactDetails({
              result: ((res && res.result) || []).map(
                (item) =>
                  item.contactJson &&
                  ContactsMapper(item, JSON.parse(item.contactJson).contact)
              )?.[0],
              totalCount: (res && res.totalCount) || 0,
            });
          }
        }
      }
    } catch (err) {
      setContactDetails({
        result: {},
        totalCount: 0,
      });
      console.error("Failed to fetch contact:", err);
    } finally {
      setIsContactDetailsLoading(false);
    }
  }, [contactId]);
  useEffect(() => {
    if (contactId) {
      getContactDetails();
      fetchTransactionData();
      fetchAgentData();
    }
  }, [contactId]);

  const fetchTransactionData = async () => {
    try {
      setTranActionDetailsLoading(true);

      const res = await GetAfterSalesTransactionByContactId(contactId);

      if (!(res && res.status && res.status !== 200)) {
        if (res && res.totalCount > 0) {
          if (res && res?.result) {
            setTranActionDetails({
              result: res?.result || [],
              totalCount: (res && res.totalCount) || 0,
            });
          }
        }
      }
    } catch (err) {
      setTranActionDetails({
        result: [],
        totalCount: 0,
      });
      console.error("Failed to fetch contact:", err);
    } finally {
      setTranActionDetailsLoading(false);
    }
  };

  const fetchAgentData = async () => {
    try {
      setIsSalesAgentLoading(true);

      const res = await GetAfterSalesAgentById(contactId);
      if (!(res && res.status && res.status !== 200)) {
        setSalesAgent({
          ...(res || {}),
        });
      }
    } catch (err) {
      setSalesAgent({});
      console.error("Failed to fetch contact:", err);
    } finally {
      setIsSalesAgentLoading(false);
    }
  };
  function getLatestMoment(dates) {
    if (!dates || !dates.length) return null;
    return dates.reduce((latest, m) => (m.isAfter(latest) ? m : latest));
  }

  const { isNewLayout: isReskinningLayout, hasNewLayout } =
    useLayoutDetection();
  const isNewLayout = isReskinningLayout && hasNewLayout;
  const dispatch = useDispatch();
  const { translate } = useTranslate("AfterSalesClientProfileView");
  const {
    theme: { palette },
  } = useSelectedTheme();
  const styles = useStyles();

  useEffect(() => {
    if (!isNewLayout) {
      dispatch(TOGGLE_LAYOUT());
    }
  }, []);

  return isNewLayout ? (
    <Box className={styles.container}>
      <CustomBreadCrumb
        breadcrumbs={[
          {
            label: translate("bread-crump-parent"),
            nonClickable: true,
          },
          {
            label: translate("bread-crump-current"),
            link: "/home/after-sales/view",
          },
        ]}
      >
        <CustomBadge
          Style={{
            padding: "4px 8px",
            borderRadius: "6px",
            lineHeight: "20px",
          }}
          label={`${contactDetails?.result?.first_name || ""} ${
            contactDetails?.result?.last_name || ""
          } ${contactDetails?.result?.company_name || ""}`}
          BackgroundColor={palette.breadcrumbs.brand_bg_hover}
          BorderColor={palette.breadcrumbs.brand_bg_hover}
          Color={palette.breadcrumbs.brand_fg_hover}
        />
      </CustomBreadCrumb>
      <PageTitle
        wrapperClasses={styles.title}
        title={translate("page-title")}
        subTitle={translate("page-subtitle")}
        hideDivider
      />
      <Box className={styles.mainContent}>
        <Box className={styles.tabWrapper}>
          <ReskinningTabs defaultIndex={0}>
            <ReskinningTabs.List>
              <ReskinningTabs.Tab>
                {translate("transacted-units")}
              </ReskinningTabs.Tab>
              <ReskinningTabs.Tab>
                {translate("activity-timeline")}
              </ReskinningTabs.Tab>
              <ReskinningTabs.Tab>{translate("payments")}</ReskinningTabs.Tab>
              <ReskinningTabs.Tab>
                {translate("linked-offers")}
              </ReskinningTabs.Tab>
              <ReskinningTabs.Tab>{translate("documents")}</ReskinningTabs.Tab>
              <ReskinningTabs.Tab>
                {translate("assigned-agents")}
              </ReskinningTabs.Tab>
            </ReskinningTabs.List>

            <ReskinningTabs.Panels>
              <ReskinningTabs.Panel>
                {!havePermissionToViewDetails([
                  AfterSalePermissions.ViewAfterSale,
                ]) ? (
                  <PermissionDeniedLayout />
                ) : (
                  <TransactionPanel
                    loading={tranActionDetailsLoading}
                    tranActionDetails={tranActionDetails}
                  />
                )}
              </ReskinningTabs.Panel>
              <ReskinningTabs.Panel>
                {!havePermissionToViewDetails([
                  AfterSalePermissions.ViewAfterSale,
                ]) ? (
                  <PermissionDeniedLayout />
                ) : (
                  <ActivityTimelinePanel contactId={contactId} />
                )}
              </ReskinningTabs.Panel>
              <ReskinningTabs.Panel>
                {!havePermissionToViewDetails([
                  AfterSalePermissions.ViewPayments,
                ]) ? (
                  <PermissionDeniedLayout />
                ) : (
                  <PaymentPanel
                    tranActionDetails={tranActionDetails}
                    contactId={contactId}
                  />
                )}
              </ReskinningTabs.Panel>
              <ReskinningTabs.Panel>
                {!havePermissionToViewDetails([
                  AfterSalePermissions.ViewAfterSale,
                ]) ? (
                  <PermissionDeniedLayout />
                ) : (
                  <OffersPanel
                    contactId={contactId}
                    setRefetchOffers={setRefetchOffers}
                    refetch={refetchOffers}
                  />
                )}
              </ReskinningTabs.Panel>
              <ReskinningTabs.Panel>
                {!havePermissionToViewDetails([
                  AfterSalePermissions.ViewDocuments,
                ]) ? (
                  <PermissionDeniedLayout />
                ) : (
                  <DocumentsPanel contactId={contactId} />
                )}
              </ReskinningTabs.Panel>
              <ReskinningTabs.Panel>
                {!havePermissionToViewDetails([
                  AfterSalePermissions.ViewAfterSale,
                ]) ? (
                  <PermissionDeniedLayout />
                ) : (
                  <AssignedAgentsPanel contactId={contactId} />
                )}
              </ReskinningTabs.Panel>
            </ReskinningTabs.Panels>
          </ReskinningTabs>
          <Box style={{ marginTop: "32px" }}>
            <ContactOverviewCommentsCard
              contactId={contactId}
              isAfterSales
              onCommentSubmitHandler={AddAfterSalesClientComment}
              onGetCommentsHandler={GetCommentsByContactId}
            />
          </Box>
        </Box>
        <Box className={styles.asideContainer}>
          {isContactDetailsLoading || tranActionDetailsLoading ? (
            <>
              <ActionsSkeletonLoader />
              <ClientProfileSkeletonLoader />
              <LastActivitySkeletonLoader />
              <AssignedAgentSkeletonLoader />
            </>
          ) : (
            <>
              <ClientActionsCard
                setRefetchOffers={setRefetchOffers}
                contact={contactDetails.result}
              />
              <ClientProfileCard contact={contactDetails.result} />
              <LastActivityCard
                transaction={{
                  ...tranActionDetails.result[0]?.portfolioOverview,
                  latestTransaction: getLatestMoment(
                    tranActionDetails.result.map((item) =>
                      moment(item.createdOn)
                    )
                  ),
                  transactionCount:
                    tranActionDetails.result[0]?.portfolioOverview
                      ?.ownedUnits || 0,
                }}
              />
              {isSalesAgentLoading && <AssignedAgentSkeletonLoader />}
              {salesAgent && !isSalesAgentLoading && (
                <AgentCard contact={salesAgent} />
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  ) : null;
}
